import { useEffect, useState } from "react";
import { getPackages } from "@/services/packages.service";

function normalizeDuration(duration) {
  const value = String(duration || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (value === "monthly" || value === "month" || value === "montly") {
    return "monthly";
  }

  if (value === "yearly" || value === "annual" || value === "annually") {
    return "yearly";
  }

  if (
    value === "halfyear" ||
    value === "halfyearly" ||
    value === "halfyearplan" ||
    value === "semiannual" ||
    value === "semiannually" ||
    value === "6months" ||
    value === "sixmonths"
  ) {
    return "halfYear";
  }

  return value;
}

const emptyPricing = {
  monthly: null,
  yearly: null,
  halfYear: null,
};

export function usePackages(isAR = false) {
  const [groupedPlans, setGroupedPlans] = useState({});
  const [employeeCounts, setEmployeeCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    async function fetchPackages() {
      try {
        setLoading(true);
        setError(null);

        const result = await getPackages();
        const rows = Array.isArray(result?.data) ? result.data : [];

        const grouped = rows.reduce((acc, item) => {
          const employeesCount = Number(item.employees_count);
          const duration = normalizeDuration(item.duration);

          if (!employeesCount || !duration || !emptyPricing.hasOwnProperty(duration)) {
            return acc;
          }

          if (!acc[employeesCount]) {
            acc[employeesCount] = {
              id: employeesCount,
              employees: employeesCount,
              features: [],
              featuresByDuration: {
                monthly: [],
                yearly: [],
                halfYear: [],
              },
              price: { ...emptyPricing },
              prices: { ...emptyPricing },
              pricing: { ...emptyPricing },
              packages: {
                monthly: null,
                yearly: null,
                halfYear: null,
              },
            };
          }

          const packagePrice = Number(item.price || 0);
          const packageFeatures = isAR
            ? item.features_ar || []
            : item.features_en || [];

          acc[employeesCount].price[duration] = packagePrice;
          acc[employeesCount].prices[duration] = packagePrice;
          acc[employeesCount].pricing[duration] = packagePrice;

          acc[employeesCount].featuresByDuration[duration] = packageFeatures;
          acc[employeesCount].packages[duration] = {
            id: item.id,
            price: packagePrice,
            features: packageFeatures,
            raw: item,
          };

          if (!acc[employeesCount].features.length) {
            acc[employeesCount].features = packageFeatures;
          }

          return acc;
        }, {});

        const counts = Object.keys(grouped)
          .map(Number)
          .sort((a, b) => a - b);

        if (active) {
          setGroupedPlans(grouped);
          setEmployeeCounts(counts);
        }
      } catch (err) {
        if (active) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load packages"
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    fetchPackages();

    return () => {
      active = false;
    };
  }, [isAR]);

  return {
    groupedPlans,
    employeeCounts,
    loading,
    error,
  };
}